<template>
  <input :id="id"
         type="text"
         :name="name"
         :value="value"
         :required="required"
         :class="classes"
         :placeholder="placeholder"
         :aria-describedby="describedby"
         autocomplete="off"
         ref="searchField"
         @input="$emit('input', $event.target.value)"/>
</template>
<script>
  export default {
    props: {
      fields: {
        type: Array,
        default: function () {
          return [
            'geometry',
            'formatted_address',
            'address_components',
            'adr_address',
            'formatted_address',
            'html_attributions',
            'name',
            'types',
            'vicinity'
          ]
        }
      },
      classes: String,
      id: String,
      // Ties the input to a visible hint ("Pick a suggestion, or drag the pin")
      // so the instruction is announced with the field rather than only seen.
      describedby: String,
      name: {
        type: String,
        default: 'address',
      },
      placeholder: String,
      required: {
        type: Boolean,
        default: true,
      },
      // The v-model half that was missing. Callers have always written
      // `v-model="art.address"` here, but with no `value` prop and no `input`
      // event that binding was inert in BOTH directions: typing never reached
      // `art.address` (so the wizard's Next button stayed disabled for anyone
      // who typed an address instead of picking a suggestion), and an address
      // written into `art.address` from elsewhere -- the reverse geocode after
      // the pin moves, the EXIF/geolocation path in handleFileUpload -- never
      // appeared in the box. That second half is what let the pin and the text
      // silently describe two different places.
      value: {
        type: String,
        default: '',
      },
    },
    data: function () {
      return {
        place: {},
        autocomplete: null,
      }
    },
    watch: {
      place(currentPlace) {
        this.$emit('placeChange', currentPlace)
      },
    },
    mounted() {
      // This is the line the feature actually hangs on.
      //
      // `vue2-google-maps` injects the Google script LAZILY: its install()
      // builds `gmapApiPromiseLazy` and comments "Use a lazy to only load the
      // API when a VGM component is loaded" (node_modules/vue2-google-maps/
      // dist/main.js:118-120). Nothing is requested until something CALLS that
      // lazy, and the only callers are VGM's own components -- GmapMap's
      // mounted() opens with `return this.$gmapApiPromiseLazy().then(...)`
      // (dist/components/mapImpl.js:139).
      //
      // This component never called it. It only READ `gmapApi`, a computed over
      // the reactive store install() creates, which stays null until somebody
      // else triggers the load. That was fine while the wizard also rendered a
      // GmapMap next to this field, and stopped being fine the moment those
      // maps became Leaflet: `window.google` was undefined on the live site,
      // there were zero googleapis script tags on the page, and Places had
      // simply never worked since. A component silently depending on a sibling's
      // side effect is the bug, so this asks for what it needs itself -- the
      // map in the wizard's location step would trigger the same load, and this
      // must not care whether it is there.
      //
      // The promise also removes a race the old `setTimeout(..., 500)` could
      // not win: at 500ms with the API still in flight, `this.google.maps`
      // threw a TypeError into an empty catch-nothing and the field stayed a
      // plain text box for the rest of the session.
      //
      // The mixin that puts $gmapApiPromiseLazy on every component is installed
      // by Vue.use(VueGoogleMaps) in packs/hello_vue.js (dist/main.js:122-127).
      if (typeof this.$gmapApiPromiseLazy !== 'function') return

      this.$gmapApiPromiseLazy()
        .then(() => this.initializeAutocomplete())
        .catch((error) => {
          // A blocked, offline or key-rejected loader leaves a usable text
          // input rather than a broken one: `art.address` is free text that the
          // API stores as typed, and the wizard's location step can also be
          // satisfied by the map pin alone.
          console.error('Google Places could not be loaded; address suggestions are off.', error)
        })
    },
    beforeDestroy() {
      if (!this.autocomplete || !window.google) return

      // The widget registers listeners on both itself and the input element,
      // and the wizard is now mounted and destroyed on every visit to '/add'
      // (LayoutBase.vue renders it with v-if), so anything left behind here
      // accumulates once per open.
      window.google.maps.event.clearInstanceListeners(this.autocomplete)
      if (this.$refs.searchField) {
        window.google.maps.event.clearInstanceListeners(this.$refs.searchField)
      }

      // The suggestion dropdown is NOT a child of this input: Places appends a
      // `.pac-container` to document.body, and it is never removed when the
      // input that owns it disappears. Only the ones that appeared after we
      // initialised are touched -- taken as a diff rather than a blanket
      // querySelectorAll, so a second Places field elsewhere on the page keeps
      // its own dropdown.
      const containers = document.querySelectorAll('.pac-container')
      Array.from(containers).forEach((container) => {
        if (!this.knownPacContainers.has(container)) container.remove()
      })
    },
    methods: {
      initializeAutocomplete() {
        // Idempotent on purpose. Two Autocomplete instances bound to one input
        // render two overlapping dropdowns and fire `place_changed` twice.
        if (this.autocomplete) return

        const input = this.$refs.searchField
        // Destroyed while the API was still loading -- a visitor who opened the
        // wizard and closed it inside a second, which is exactly what a
        // mis-click looks like.
        if (!input) return

        this.knownPacContainers = new Set(document.querySelectorAll('.pac-container'))

        this.autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['geocode'],
          fields: this.fields,
        })

        this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete.getPlace()

          // Places writes the chosen address straight into the DOM input,
          // behind Vue's back. Echoing it out as `input` keeps the parent's
          // bound value in step, so the next re-render cannot patch the box
          // back to whatever was in it before the visitor chose.
          if (input.value !== this.value) this.$emit('input', input.value)

          this.place = Object.assign({}, place)
        })
      },
    }
  }
</script>
